import type { Level } from '@color-dots/game';
import {
  DIFFICULTY_TIERS,
  filterLevelsByTier,
  type DifficultyTier,
} from '../settings.js';
import {
  getStylesPresentInLevels,
  getVisualStyleForLevel,
  getVisualStyleLabel,
  groupLevelsByVisualStyle,
  STYLE_ICONS,
  type VisualStyle,
} from '../game/visualStyles.js';
import {
  getTotalStars,
  getUnlockedCollectibleIds,
  loadCollectibles,
  type CollectibleDef,
} from '../collection.js';
import { loadWordManifest } from '../words/loader.js';
import {
  getTopicCompletionCount,
  setLastWordTopic,
} from '../words/progress.js';
import type { WordTopic } from '../words/types.js';
import { getStoredPlayer, registerPlayer, syncScore, type PlayerAuth } from '../auth.js';
import { fetchLeaderboard, type LeaderboardEntry } from '../leaderboard.js';

export type LevelPickerTab = 'colors' | 'words' | 'collection' | 'leaderboard';
export type SpecialMode = 'daily' | 'endless';

export interface DailyPickerInfo {
  streak: number;
  completedToday: boolean;
}

export interface EndlessPickerInfo {
  bestStreak: number;
}

export interface LevelPickerConfig {
  levels: Level[];
  currentLevelId: string;
  completedLevelIds: Set<string>;
  difficultyTier: DifficultyTier;
  initialTab?: LevelPickerTab;
  dailyInfo?: DailyPickerInfo;
  endlessInfo?: EndlessPickerInfo;
  onSelectLevel: (levelId: string) => void;
  onSelectSpecial?: (mode: SpecialMode) => void;
  onDifficultyChange: (tier: DifficultyTier) => void;
  onClose: () => void;
}

function renderSpecialModesRow(daily?: DailyPickerInfo, endless?: EndlessPickerInfo): string {
  if (!daily && !endless) return '';

  const dailyCard = daily
    ? `
      <button type="button" class="level-picker-special-card" data-special="daily">
        <span class="level-picker-special-icon" aria-hidden="true">📅</span>
        <span class="level-picker-special-body">
          <span class="level-picker-special-title">Уровень дня</span>
          <span class="level-picker-special-meta">${
            daily.completedToday ? 'Пройден сегодня ✓' : daily.streak > 0 ? `Серия: ${daily.streak}` : 'Новая головоломка каждый день'
          }</span>
        </span>
      </button>
    `
    : '';

  const endlessCard = endless
    ? `
      <button type="button" class="level-picker-special-card" data-special="endless">
        <span class="level-picker-special-icon" aria-hidden="true">♾️</span>
        <span class="level-picker-special-body">
          <span class="level-picker-special-title">Бесконечный режим</span>
          <span class="level-picker-special-meta">${
            endless.bestStreak > 0 ? `Лучшая серия: ${endless.bestStreak}` : 'Играй, пока получается'
          }</span>
        </span>
      </button>
    `
    : '';

  return `<div class="level-picker-special">${dailyCard}${endlessCard}</div>`;
}

function formatLevelNumber(levelId: string): string {
  return levelId.replace('e-', '');
}

function renderLevelCell(
  level: Level,
  currentLevelId: string,
  completedLevelIds: Set<string>,
): string {
  const num = formatLevelNumber(level.id);
  const isActive = level.id === currentLevelId;
  const isCompleted = completedLevelIds.has(level.id);
  const style = getVisualStyleForLevel(level.id);

  return `
    <button
      type="button"
      class="level-picker-cell${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}"
      data-level-id="${level.id}"
      title="${getVisualStyleLabel(style)} · ${level.size}×${level.size}"
      aria-label="Уровень ${num}${isCompleted ? ', пройден' : ''}${isActive ? ', текущий' : ''}"
    >
      <span class="level-picker-cell-num">${num}</span>
      ${isCompleted ? '<span class="level-picker-cell-check" aria-hidden="true">✓</span>' : ''}
    </button>
  `;
}

function renderStyleFilterChips(
  styles: VisualStyle[],
  activeStyle: VisualStyle | 'all',
): string {
  const allChip = `
    <button
      type="button"
      class="level-picker-chip${activeStyle === 'all' ? ' active' : ''}"
      data-style="all"
    >Все стили</button>
  `;

  const styleChips = styles.map(
    (style) => `
      <button
        type="button"
        class="level-picker-chip${activeStyle === style ? ' active' : ''}"
        data-style="${style}"
      >${STYLE_ICONS[style]} ${getVisualStyleLabel(style)}</button>
    `,
  );

  return allChip + styleChips.join('');
}

function renderDifficultyChips(activeTier: DifficultyTier): string {
  return DIFFICULTY_TIERS.map(
    (tier) => `
      <button
        type="button"
        class="level-picker-chip level-picker-chip--tier${activeTier === tier.id ? ' active' : ''}"
        data-tier="${tier.id}"
        title="${tier.description}"
      >${tier.label}</button>
    `,
  ).join('');
}

function renderColorsPanel(
  levels: Level[],
  currentLevelId: string,
  completedLevelIds: Set<string>,
  difficultyTier: DifficultyTier,
  styleFilter: VisualStyle | 'all',
): string {
  const filtered = filterLevelsByTier(levels, difficultyTier);
  const visible =
    styleFilter === 'all'
      ? filtered
      : filtered.filter((level) => getVisualStyleForLevel(level.id) === styleFilter);

  const tierInfo = DIFFICULTY_TIERS.find((t) => t.id === difficultyTier)!;
  const styles = getStylesPresentInLevels(filtered);

  let sectionsHtml: string;

  if (styleFilter === 'all') {
    const groups = groupLevelsByVisualStyle(visible);
    sectionsHtml = groups
      .map(
        (group) => `
          <section class="level-picker-section">
            <h3 class="level-picker-section-title">
              <span class="level-picker-section-icon" aria-hidden="true">${group.icon}</span>
              ${group.label}
              <span class="level-picker-section-meta">${group.levels.length}</span>
            </h3>
            <div class="level-picker-grid">
              ${group.levels.map((level) => renderLevelCell(level, currentLevelId, completedLevelIds)).join('')}
            </div>
          </section>
        `,
      )
      .join('');
  } else {
    sectionsHtml = `
      <section class="level-picker-section">
        <h3 class="level-picker-section-title">
          <span class="level-picker-section-icon" aria-hidden="true">${STYLE_ICONS[styleFilter]}</span>
          ${getVisualStyleLabel(styleFilter)}
          <span class="level-picker-section-meta">${visible.length}</span>
        </h3>
        <div class="level-picker-grid">
          ${visible.map((level) => renderLevelCell(level, currentLevelId, completedLevelIds)).join('')}
        </div>
      </section>
    `;
  }

  const completedInTier = filtered.filter((l) => completedLevelIds.has(l.id)).length;

  return `
    <div class="level-picker-filters">
      <div class="level-picker-filter-group">
        <span class="level-picker-filter-label">Сложность</span>
        <div class="level-picker-chips level-picker-chips--wrap" role="group" aria-label="Сложность">
          ${renderDifficultyChips(difficultyTier)}
        </div>
        <p class="level-picker-filter-hint">${tierInfo.description} · пройдено ${completedInTier}/${filtered.length}</p>
      </div>
      <div class="level-picker-filter-group">
        <span class="level-picker-filter-label">Визуальный стиль</span>
        <div class="level-picker-chips" role="group" aria-label="Визуальный стиль">
          ${renderStyleFilterChips(styles, styleFilter)}
        </div>
      </div>
    </div>
    <div class="level-picker-sections">
      ${visible.length > 0 ? sectionsHtml : '<p class="level-picker-empty">Нет уровней для выбранных фильтров</p>'}
    </div>
  `;
}

function renderTopicCard(topic: WordTopic): string {
  const completed = getTopicCompletionCount(topic.id);
  const total = topic.levelCount;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const isComingSoon = topic.status === 'coming-soon';

  return `
    <button
      type="button"
      class="level-picker-topic${isComingSoon ? ' level-picker-topic--soon' : ''}"
      data-topic-id="${topic.id}"
      ${isComingSoon ? 'disabled' : ''}
    >
      <span class="level-picker-topic-icon" aria-hidden="true">${topic.icon}</span>
      <span class="level-picker-topic-body">
        <span class="level-picker-topic-name">${topic.title.ru}</span>
        <span class="level-picker-topic-meta">
          ${isComingSoon ? 'Скоро' : `${completed}/${total} уровней · ${pct}%`}
        </span>
      </span>
      ${!isComingSoon ? '<span class="level-picker-topic-arrow" aria-hidden="true">→</span>' : ''}
    </button>
  `;
}

function renderWordsPanel(topics: WordTopic[]): string {
  const available = topics.filter((t) => t.status === 'available');
  const comingSoon = topics.filter((t) => t.status === 'coming-soon');

  return `
    <p class="level-picker-words-intro">
      Соединяй слова с переводами и категориями. Каждая тема — отдельный набор уровней.
    </p>
    <div class="level-picker-topics">
      ${available.map(renderTopicCard).join('')}
      ${
        comingSoon.length > 0
          ? `
        <div class="level-picker-topics-soon">
          <span class="level-picker-filter-label">Скоро</span>
          ${comingSoon.map(renderTopicCard).join('')}
        </div>
      `
          : ''
      }
    </div>
  `;
}

function renderCollectibleCard(collectible: CollectibleDef, unlocked: boolean): string {
  return `
    <div class="collectible-card${unlocked ? ' unlocked' : ' locked'}">
      <div class="collectible-card-image">
        <img src="${collectible.image}" alt="${unlocked ? collectible.name : 'Заблокировано'}" loading="lazy" />
        ${!unlocked ? '<span class="collectible-card-lock" aria-hidden="true">🔒</span>' : ''}
      </div>
      <span class="collectible-card-name">${unlocked ? collectible.name : '???'}</span>
      <span class="collectible-card-threshold">${unlocked ? 'Открыто' : `${collectible.threshold}★`}</span>
    </div>
  `;
}

function renderCollectionPanel(collectibles: CollectibleDef[], maxStars: number): string {
  const totalStars = getTotalStars();
  const unlocked = getUnlockedCollectibleIds(collectibles, totalStars);

  return `
    <p class="level-picker-collection-intro">
      Собирай духов-хранителей стилей за звёзды. Всего звёзд: <strong>${totalStars}</strong> из ${maxStars}.
    </p>
    <div class="level-picker-collection-grid">
      ${collectibles.map((c) => renderCollectibleCard(c, unlocked.has(c.id))).join('')}
    </div>
  `;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderLeaderboardPanel(
  player: PlayerAuth | null,
  entries: LeaderboardEntry[] | null,
  myRank: number | null,
  error: string | null,
  busy: boolean,
): string {
  const top = player
    ? `<p class="level-picker-leaderboard-me">Играешь как <strong>${escapeHtml(player.nickname)}</strong>${myRank ? ` · место ${myRank}` : ''}</p>`
    : `
      <p class="level-picker-leaderboard-intro">
        Придумай ник и сравнивай счёт с другими — без пароля, только имя на этом устройстве.
      </p>
      <form class="level-picker-leaderboard-join" id="leaderboard-join-form">
        <input
          type="text"
          class="level-picker-leaderboard-input"
          id="leaderboard-nickname-input"
          placeholder="Ник (2–20 символов)"
          maxlength="20"
          autocomplete="off"
          ${busy ? 'disabled' : ''}
        />
        <button type="submit" class="action-btn primary" ${busy ? 'disabled' : ''}>
          ${busy ? 'Загрузка…' : 'Участвовать'}
        </button>
      </form>
      ${error ? `<p class="level-picker-leaderboard-error">${escapeHtml(error)}</p>` : ''}
    `;

  if (!entries) {
    return `${top}<p class="level-picker-loading">Загрузка таблицы лидеров…</p>`;
  }

  const rows = entries.length
    ? entries
        .map(
          (e) => `
        <div class="leaderboard-row${player && e.nickname === player.nickname ? ' leaderboard-row--me' : ''}">
          <span class="leaderboard-rank">${e.rank}</span>
          <span class="leaderboard-nickname">${escapeHtml(e.nickname)}</span>
          <span class="leaderboard-stars">${e.totalStars}★</span>
        </div>
      `,
        )
        .join('')
    : '<p class="level-picker-empty">Пока никого нет — стань первым!</p>';

  return `${top}<div class="leaderboard-list">${rows}</div>`;
}

export function openLevelPicker(config: LevelPickerConfig): HTMLElement {
  let activeTab: LevelPickerTab = config.initialTab ?? 'colors';
  let difficultyTier = config.difficultyTier;
  let styleFilter: VisualStyle | 'all' = 'all';
  let wordTopics: WordTopic[] | null = null;
  let collectibles: CollectibleDef[] | null = null;
  let leaderboardEntries: LeaderboardEntry[] | null = null;
  let leaderboardMyRank: number | null = null;
  let leaderboardError: string | null = null;
  let leaderboardBusy = false;

  const overlay = document.createElement('div');
  overlay.className = 'level-picker-overlay';

  const render = (): void => {
    const colorsPanel =
      activeTab === 'colors'
        ? renderSpecialModesRow(config.dailyInfo, config.endlessInfo) +
          renderColorsPanel(
            config.levels,
            config.currentLevelId,
            config.completedLevelIds,
            difficultyTier,
            styleFilter,
          )
        : '';

    const wordsPanel =
      activeTab === 'words' && wordTopics
        ? renderWordsPanel(wordTopics)
        : activeTab === 'words'
          ? '<p class="level-picker-loading">Загрузка тем…</p>'
          : '';

    const collectionPanel =
      activeTab === 'collection' && collectibles
        ? renderCollectionPanel(collectibles, config.levels.length * 3)
        : activeTab === 'collection'
          ? '<p class="level-picker-loading">Загрузка коллекции…</p>'
          : '';

    const leaderboardPanel =
      activeTab === 'leaderboard'
        ? renderLeaderboardPanel(
            getStoredPlayer(),
            leaderboardEntries,
            leaderboardMyRank,
            leaderboardError,
            leaderboardBusy,
          )
        : '';

    overlay.innerHTML = `
      <div class="level-picker-modal" role="dialog" aria-modal="true" aria-labelledby="level-picker-title">
        <header class="level-picker-header">
          <h2 id="level-picker-title">Выбор уровня</h2>
          <button type="button" class="level-picker-close" id="level-picker-close" aria-label="Закрыть">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </header>

        <nav class="level-picker-tabs" role="tablist" aria-label="Режим игры">
          <button
            type="button"
            class="level-picker-tab${activeTab === 'colors' ? ' active' : ''}"
            role="tab"
            aria-selected="${activeTab === 'colors'}"
            data-tab="colors"
          >🎨 Цвета</button>
          <button
            type="button"
            class="level-picker-tab${activeTab === 'words' ? ' active' : ''}"
            role="tab"
            aria-selected="${activeTab === 'words'}"
            data-tab="words"
          >📚 Слова</button>
          <button
            type="button"
            class="level-picker-tab${activeTab === 'collection' ? ' active' : ''}"
            role="tab"
            aria-selected="${activeTab === 'collection'}"
            data-tab="collection"
          >🎁 Коллекция</button>
          <button
            type="button"
            class="level-picker-tab${activeTab === 'leaderboard' ? ' active' : ''}"
            role="tab"
            aria-selected="${activeTab === 'leaderboard'}"
            data-tab="leaderboard"
          >🏆 Топ</button>
        </nav>

        <div class="level-picker-body">
          <div class="level-picker-panel" data-panel="colors" ${activeTab !== 'colors' ? 'hidden' : ''}>
            ${colorsPanel}
          </div>
          <div class="level-picker-panel" data-panel="words" ${activeTab !== 'words' ? 'hidden' : ''}>
            ${wordsPanel}
          </div>
          <div class="level-picker-panel" data-panel="collection" ${activeTab !== 'collection' ? 'hidden' : ''}>
            ${collectionPanel}
          </div>
          <div class="level-picker-panel" data-panel="leaderboard" ${activeTab !== 'leaderboard' ? 'hidden' : ''}>
            ${leaderboardPanel}
          </div>
        </div>
      </div>
    `;

    bindEvents();
  };

  const loadLeaderboard = (): void => {
    const player = getStoredPlayer();
    const syncPromise = player ? syncScore().catch(() => null) : Promise.resolve(null);
    void syncPromise
      .then((sync) => {
        leaderboardMyRank = sync?.rank ?? null;
        return fetchLeaderboard();
      })
      .then((entries) => {
        leaderboardEntries = entries;
        render();
      })
      .catch(() => {
        const panel = overlay.querySelector('[data-panel="leaderboard"]');
        if (panel) {
          panel.innerHTML = '<p class="level-picker-empty">Не удалось загрузить таблицу лидеров</p>';
        }
      });
  };

  const bindEvents = (): void => {
    overlay.querySelector('#level-picker-close')?.addEventListener('click', () => {
      config.onClose();
      overlay.remove();
    });

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) {
        config.onClose();
        overlay.remove();
      }
    });

    overlay.querySelectorAll('.level-picker-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tab = (btn as HTMLElement).dataset.tab as LevelPickerTab;
        if (tab === activeTab) return;
        activeTab = tab;
        if (tab === 'words' && !wordTopics) {
          void loadWordManifest()
            .then((manifest) => {
              wordTopics = manifest.topics;
              render();
            })
            .catch(() => {
              const panel = overlay.querySelector('[data-panel="words"]');
              if (panel) {
                panel.innerHTML =
                  '<p class="level-picker-empty">Не удалось загрузить темы</p>';
              }
            });
        }
        if (tab === 'collection' && !collectibles) {
          void loadCollectibles()
            .then((loaded) => {
              collectibles = loaded;
              render();
            })
            .catch(() => {
              const panel = overlay.querySelector('[data-panel="collection"]');
              if (panel) {
                panel.innerHTML =
                  '<p class="level-picker-empty">Не удалось загрузить коллекцию</p>';
              }
            });
        }
        if (tab === 'leaderboard' && !leaderboardEntries) {
          loadLeaderboard();
        }
        render();
      });
    });

    overlay.querySelector('#leaderboard-join-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = overlay.querySelector('#leaderboard-nickname-input') as HTMLInputElement | null;
      const nickname = input?.value.trim() ?? '';
      if (!nickname || leaderboardBusy) return;

      leaderboardBusy = true;
      leaderboardError = null;
      render();

      void registerPlayer(nickname)
        .then(() => {
          leaderboardBusy = false;
          leaderboardEntries = null;
          render();
          loadLeaderboard();
        })
        .catch((err) => {
          leaderboardBusy = false;
          leaderboardError = err instanceof Error ? err.message : 'Не удалось зарегистрироваться';
          render();
        });
    });

    overlay.querySelectorAll('[data-tier]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tier = (btn as HTMLElement).dataset.tier as DifficultyTier;
        if (tier === difficultyTier) return;
        difficultyTier = tier;
        config.onDifficultyChange(tier);
        render();
      });
    });

    overlay.querySelectorAll('[data-style]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const style = (btn as HTMLElement).dataset.style as VisualStyle | 'all';
        if (style === styleFilter) return;
        styleFilter = style;
        render();
      });
    });

    overlay.querySelectorAll('[data-level-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const levelId = (btn as HTMLElement).dataset.levelId;
        if (!levelId) return;
        config.onSelectLevel(levelId);
        config.onClose();
        overlay.remove();
      });
    });

    overlay.querySelectorAll('[data-special]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = (btn as HTMLElement).dataset.special as SpecialMode;
        if (!mode || !config.onSelectSpecial) return;
        config.onSelectSpecial(mode);
        config.onClose();
        overlay.remove();
      });
    });

    overlay.querySelectorAll('[data-topic-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const topicId = (btn as HTMLElement).dataset.topicId;
        if (!topicId || (btn as HTMLButtonElement).disabled) return;
        setLastWordTopic(topicId);
        window.location.hash = '#words';
        window.location.reload();
      });
    });
  };

  render();
  document.body.appendChild(overlay);

  const activeCell = overlay.querySelector('.level-picker-cell.active');
  if (activeCell instanceof HTMLElement) {
    requestAnimationFrame(() => {
      activeCell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  return overlay;
}
